import {
  build_selected_questions_from_template,
  get_default_metric_names,
  get_default_report_field_names,
  is_report_field_allowed,
} from "./report_generation_builder_helpers";

/**
 * Core state and utility methods for the report builder.
 */
export default {
  normalize_id(value) {
    const parsed = Number(value || 0);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  },
  get_lookup_id(key, internal_name) {
    const items = Reflect.get(this.lookup_data, key) || [];
    const item = items.find((entry) => entry.internal_name === internal_name);
    return item ? item.id : null;
  },
  get_route_template_id() {
    const parsed = Number(this.$route?.query?.template_id || 0);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  },
  get_report_fields(report_type_internal_name) {
    const report_fields = Reflect.get(this.lookup_data, "report_fields") || [];
    return report_fields.filter((field) =>
      is_report_field_allowed(report_type_internal_name, field.internal_name)
    );
  },
  translate_label(label) {
    return label;
  },
  get_default_report_type_id() {
    const preferred = this.report_types.find(
      (report_type) => report_type.internal_name === "entity_profile_detail"
    );
    return preferred?.id || this.report_types[0]?.id || "";
  },
  get_default_output_format_id() {
    const preferred = this.get_lookup_id("output_formats", "xlsx");
    return preferred || this.get_lookup_id("output_formats", "csv") || "";
  },
  get_default_field_ids(report_type_internal_name) {
    const default_names = get_default_report_field_names(report_type_internal_name);
    return this.get_report_fields(report_type_internal_name)
      .filter((field) => default_names.includes(field.internal_name))
      .map((field) => field.id);
  },
  initialize_defaults() {
    this.selected_report_type_id = this.get_default_report_type_id();
    this.selected_output_format_id = this.get_default_output_format_id();
    if (this.initial_entity_type_id_value) {
      this.selected_entity_type_ids = [this.initial_entity_type_id_value];
    }
    this.sync_selected_fields(this.selected_report_type_internal_name);
  },
  sync_selected_fields(report_type_internal_name) {
    const allowed_field_ids = this.get_report_fields(report_type_internal_name).map(
      (field) => field.id
    );
    const selected_field_ids = this.selected_field_ids.filter((field_id) =>
      allowed_field_ids.includes(this.normalize_id(field_id))
    );
    this.selected_field_ids = selected_field_ids.length
      ? selected_field_ids.map((field_id) => this.normalize_id(field_id))
      : this.get_default_field_ids(report_type_internal_name);
    this.prune_field_display_labels();
    this.sync_response_summary_answer_fields(null);
  },
  prune_field_display_labels() {
    const selected_id_set = new Set(this.selected_field_ids);
    const entries = Object.entries(this.field_display_labels).filter(
      ([field_id]) => selected_id_set.has(this.normalize_id(field_id))
    );
    this.field_display_labels = Object.fromEntries(entries);
  },
  update_field_display_label(field_id, value) {
    const normalized_field_id = this.normalize_id(field_id);
    const trimmed_value = String(value || "").trim();
    if (!normalized_field_id) {
      return;
    }
    if (!trimmed_value) {
      Reflect.deleteProperty(this.field_display_labels, normalized_field_id);
      return;
    }
    Reflect.set(this.field_display_labels, normalized_field_id, trimmed_value);
  },
  sync_selected_date_dimension() {
    const allowed_ids = this.allowed_date_dimensions.map(
      (date_dimension) => date_dimension.id
    );
    const selected_date_dimension_id = this.normalize_id(
      this.selected_date_dimension_id
    );
    if (allowed_ids.includes(selected_date_dimension_id)) {
      return;
    }
    this.selected_date_dimension_id = "";
    this.selected_date_from = "";
    this.selected_date_to = "";
  },
  sync_question_metric_selections() {
    if (!this.is_question_statistics_summary) {
      return;
    }
    this.selected_questions = this.selected_questions.map((question) => ({
      ...question,
      selected_metric_type_ids: (question.selected_metric_type_ids || []).length
        ? question.selected_metric_type_ids
        : this.get_default_metric_type_ids(question.question_type),
    }));
  },
  reset_hierarchy() {
    this.selected_form_id = "";
    this.selected_main_section_id = "";
    this.selected_sub_section_id = "";
    this.main_sections = [];
    this.sub_sections = [];
    this.question_list = [];
    this.available_forms = this.is_question_scope_report ? this.available_forms : [];
    this.question_search = "";
  },
  prune_selected_questions() {
    if (!this.template_entity_type_ids.length || !this.is_question_scope_report) {
      return;
    }
    this.selected_questions = this.selected_questions.filter((question) =>
      question.entity_type_ids.some((id) => this.template_entity_type_ids.includes(id))
    );
  },
  build_form_params() {
    const params = { project_id: this.project_id };
    if (this.template_entity_type_ids.length) {
      params.entity_type_ids = this.template_entity_type_ids.join(",");
    }
    return params;
  },
  hydrate_template_field_display_labels(columns) {
    return (columns || []).reduce((accumulator, column) => {
      if (column.report_field?.id && column.display_label) {
        Reflect.set(accumulator, column.report_field.id, column.display_label);
      }
      return accumulator;
    }, {});
  },
  hydrate_template_questions(template) {
    if (!this.is_question_scope_report) {
      return [];
    }
    return build_selected_questions_from_template(
      template.filters,
      template.question_metrics
    );
  },
  get_default_metric_type_ids(question_type) {
    const default_names = get_default_metric_names(question_type);
    return this.report_metric_types
      .filter((metric_type) => default_names.includes(metric_type.internal_name))
      .map((metric_type) => metric_type.id);
  },
  get_field_display_label(field_id) {
    const normalized_field_id = this.normalize_id(field_id);
    return String(Reflect.get(this.field_display_labels, normalized_field_id) || "");
  },
  get_preview_column_label(field) {
    const custom_label = this.get_field_display_label(field.id).trim();
    return custom_label || field.display_name;
  },
  handle_form_selection(form_id) {
    this.selected_form_id = form_id;
    this.load_main_sections();
  },
  handle_main_section_selection(main_section_id) {
    this.selected_main_section_id = main_section_id;
    this.load_sub_sections();
  },
  handle_sub_section_selection(sub_section_id) {
    this.selected_sub_section_id = sub_section_id;
    this.load_questions();
  },
  handle_question_search_update(value) {
    this.question_search = value;
  },
  handle_field_display_label_change(payload) {
    this.update_field_display_label(payload.field_id, payload.value);
  },
};
